import {
  SocialPlateforms,
  useTranslation,
  useToast,
  Notification,
  Theme,
  useBreakpoint,
  Breakpoints,
} from '@nextgen-web-framework/all';
import { ConsoleLogger, ConfigService } from '@nextgen-web-framework/core';
import {
  getSharePath,
  createSharecode,
  publishShareEvent,
  getSocialLinksConfig,
} from '../../utils/sharebar/sharebarHelpers';
import SocialLink from '../SocialLink/SocialLink';
import { SocialLinksProps } from './types/propTypes';
import styles from './SocialLinks.module.scss';

const SocialLinks = ({
  socialIconsVisibility,
  existingSharecode,
  linkProps,
  shareTextMessage,
  regType,
  socialLinkHandler,
}: SocialLinksProps) => {
  const t = useTranslation();
  const viewPortType = useBreakpoint();
  const isMobile = viewPortType === Breakpoints.XS || viewPortType === Breakpoints.SM;
  const { addToast, deleteToast } = useToast();
  const configs = ConfigService.getConfigs();
  const id = 'link_copied_toast_id';
  const successMsg = t('web.link.copied.success.message');
  const failureMsg = t('web.link.copied.failure.message');
  const lineMsg = t('web.line.message');
  const whatsappMsg = t('web.whatsapp.message');
  const emailSubjectMsg = t('web.email.subject');
  const emailBodyMsg = t('web.email.body');
  const showToastMessage = (success: boolean) => {
    addToast({
      id,
      timeout: 1000,
      content: (
        <Notification
          theme={success ? Theme.SUCCESS : Theme.ERROR}
          onCloseClick={() => deleteToast(id)}
          message={success ? successMsg : failureMsg}
        />
      ),
    });
  };
  const fallbackCopyTextToClipboard = (text: string) => {
    if (document.queryCommandSupported && document.queryCommandSupported('copy')) {
      const tempInput = document.createElement('input');
      tempInput.value = text;
      document.body.appendChild(tempInput);
      tempInput.select();

      try {
        document.execCommand('copy');
        showToastMessage(true);
      } catch (err) {
        ConsoleLogger.error('Document Failed to copy: ', err, text);
        showToastMessage(false);
      } finally {
        document.body.removeChild(tempInput);
      }
    } else {
      ConsoleLogger.error('Clipboard document copy not supported');
      showToastMessage(false);
    }
  };

  const getShareLinkUrl = async (iconName: SocialPlateforms) => {
    let sharecode = '';
    let shareLink = '';
    try {
      if (existingSharecode) {
        sharecode = `${existingSharecode}?utm_source=${iconName}`;
      } else {
        sharecode = (await createSharecode({ linkProps, sharingChannel: iconName, regType }))?.sharecode as string;
      }
      publishShareEvent(linkProps, iconName);
      shareLink = getSharePath(sharecode, configs.NEXT_SHARELINK_DOMAIN as string, socialIconsVisibility.sharelinkPath);
      return shareLink;
    } catch (error) {
      ConsoleLogger.error('Error fetching sharecode or generating share link URL', error);
    }
    return '';
  };

  const showMessage = (text: string) => (text ? showToastMessage(true) : showToastMessage(false));
  const handleCopyLink = (iconName: SocialPlateforms) => {
    /**
     * Safari restricts the Clipboard API to only work when triggered by user interaction.
     * This prevents performing async operations.
     * To handle this scenario, we are using `ClipboardItem` for browsers that support it,
     * and an alternative approach for browsers where `ClipboardItem` is not available.
     */
    let sharelinkUrl = '';
    if (typeof ClipboardItem && navigator?.clipboard?.write) {
      try {
        const text = new ClipboardItem({
          'text/plain': getShareLinkUrl(iconName).then((text) => {
            sharelinkUrl = text;
            showMessage(text);
            return new Blob([text], { type: 'text/plain' });
          }),
        });
        navigator.clipboard.write([text]);
      } catch (err) {
        fallbackCopyTextToClipboard(sharelinkUrl);
      }
    } else {
      getShareLinkUrl(iconName)
        .then((text) => {
          sharelinkUrl = text;
          showMessage(text);
          if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(sharelinkUrl);
          } else {
            fallbackCopyTextToClipboard(sharelinkUrl);
          }
        })
        .catch((err) => {
          ConsoleLogger.error('Failed to copy: ', err);
          fallbackCopyTextToClipboard(sharelinkUrl);
        });
    }
  };
  const socialIconClickHandler = async (iconName: SocialPlateforms) => {
    if (socialLinkHandler?.()) {
      return;
    }
    let sharelinkUrl = '';
    if (iconName !== SocialPlateforms.LINK) {
      sharelinkUrl = await getShareLinkUrl(iconName);
      if (!sharelinkUrl) {
        showToastMessage(false);
        return;
      }
    }

    const message = shareTextMessage?.platformSpecific?.[iconName] || shareTextMessage?.message || '';
    switch (iconName) {
      case SocialPlateforms.MORE: {
        const shareData = {
          title: linkProps?.title,
          text: linkProps?.title,
          url: sharelinkUrl,
        };
        navigator.share(shareData);
        break;
      }
      case SocialPlateforms.LINE: {
        const lineFullUrl = `${socialIconsVisibility.lineUrl}${encodeURIComponent(
          `${sharelinkUrl}`,
        )}&text=${encodeURIComponent(message || lineMsg)}`;
        window?.open(lineFullUrl);
        break;
      }
      case SocialPlateforms.WHATSAPP: {
        const shareMessage = message || whatsappMsg;
        const textMessage = `${shareMessage} ` + `${sharelinkUrl}`;
        const whatsappFullUrl = `${socialIconsVisibility.whatsappUrl}${encodeURIComponent(textMessage)}`;
        window?.open(whatsappFullUrl);
        break;
      }
      case SocialPlateforms.EMAIL: {
        const emailSubjects = shareTextMessage?.email?.subject || emailSubjectMsg;
        const emailBodys = `${shareTextMessage?.email?.body || emailBodyMsg} ${sharelinkUrl}`;
        const emailLink = `mailto:?subject=${emailSubjects}&body=${encodeURIComponent(emailBodys)}`;
        window.location.href = emailLink;
        break;
      }

      case SocialPlateforms.FACEBOOK: {
        const fullUrl = `${socialIconsVisibility.fbUrl}${encodeURIComponent(`${sharelinkUrl}`)}`;
        window.open(fullUrl);
        break;
      }
      case SocialPlateforms.LINK: {
        handleCopyLink(iconName);
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className={styles['sharing-channel-wrapper']} data-testid="social-links-test-id">
      {getSocialLinksConfig(socialIconsVisibility, isMobile).map(
        ({ type, enabled, titleKey }) =>
          enabled && (
            <SocialLink key={type} iconName={type} iconTitle={t(titleKey)} iconClickHandler={socialIconClickHandler} />
          ),
      )}
    </div>
  );
};

export default SocialLinks;
